// Choosing a password in the CRM.
//
// ⚠️ THE CRM HAD NO WAY TO SET ONE (found 2026-10-05). "Forgot password?"
// emailed a link that signed the coach in and never asked for a new password,
// so the forgotten one stayed the password and the next sign-out locked them
// out again. A team invite said "Set your password and get in" and nothing let
// them set one. Two ways in now:
//   · arriving from a reset or invite link (modules/auth-link.js): the box
//     opens by itself, and only Save or Sign out closes it;
//   · Settings → Security → Password → Change password.
//
// ⚠️ TWO-STEP SIGN-IN. Supabase refuses a password change from a session that
// has not passed the authenticator code when the account has one set up. A
// reset link gives exactly that session, and so does a device ticked "Trust
// this device" at sign-in (login.js skips the code there), so the box asks for
// the 6-digit code first whenever the account needs it.
//
// Imported by app.js for its side effects, the way mfa.js is.

import { getSupabase } from './config.js';
import { escHtml, showToast } from './utils.js';
import { linkNeedsPassword, passwordNoLongerOwed, takeLinkError } from './auth-link.js';

export const MIN_LENGTH = 8;

const WORDS = {
  recovery: {
    title: 'Choose a new password',
    lede: 'You signed in from your reset email. Choose the password you will use to sign in from now on.',
  },
  invite: {
    title: 'Set your password',
    lede: 'Choose the password you will use to sign in to FitOps Pro.',
  },
  change: {
    title: 'Change your password',
    lede: 'Choose a new password for signing in to FitOps Pro. You stay signed in here.',
  },
};

/** What is wrong with these two entries, in words, or null when they will do. */
export function passwordProblem(pw, again) {
  if (!pw || pw.length < MIN_LENGTH) return `Use at least ${MIN_LENGTH} characters.`;
  if (pw !== again) return "The two passwords don't match.";
  return null;
}

/** Supabase's refusal, in words a coach can act on. */
export function saveErrorWords(error) {
  const code = error?.code || '';
  if (code === 'same_password') return "That's already your password. Choose a different one.";
  if (code === 'weak_password') return 'Choose a longer password, or mix in numbers and symbols.';
  if (code === 'reauthentication_needed') return 'For your security, sign out, then use Forgot password? on the sign-in page to choose a new one.';
  if (code === 'insufficient_aal') return 'Enter the 6-digit code from your authenticator app first.';
  return "Couldn't save your password. Try again.";
}

/** @type {{ promise: Promise<boolean> } | null} the box on screen, if any */
let _open = null;

/**
 * Open the password box.
 *
 * @param {{ reason: 'recovery'|'invite'|'change' }} opts
 *   recovery / invite: opened by the link, closes only on Save or Sign out.
 *   change: from Settings, with Cancel.
 * @returns {Promise<boolean>} true once a password is saved, false if closed without one
 */
export function openPasswordDialog({ reason }) {
  if (_open) return _open.promise;
  const words = WORDS[reason] || WORDS.change;
  const canClose = reason === 'change';

  const overlay = document.createElement('div');
  // crm-dialog-overlay: above every other overlay, including the "2FA
  // required" screen mfa.js can put up, so a password is chosen first.
  overlay.className = 'modal-overlay crm-dialog-overlay';
  overlay.id = 'password-modal';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-labelledby', 'password-modal-title');
  // ⚠️ .crm-dialog, NOT .modal-content. The CRM has no styles for
  // .modal-content at all, so the first build of this box (2026-10-05) showed
  // its words straight on the dimmed page between a white header and footer.
  // .crm-dialog is the CRM's own small dialog card (styles.css), in both themes.
  overlay.innerHTML = `
    <form class="crm-dialog" id="password-form" novalidate>
      <div style="display:flex;align-items:flex-start;justify-content:space-between;gap:12px;">
        <h2 class="crm-dialog-title" id="password-modal-title">${escHtml(words.title)}</h2>
        ${canClose ? '<button type="button" class="modal-close" data-pw-close aria-label="Close" style="margin:-6px -8px 0 0;">&times;</button>' : ''}
      </div>
      <p class="crm-dialog-msg">${escHtml(words.lede)}</p>
      <input type="email" id="pw-username" autocomplete="username" hidden />
      <div class="form-group">
        <label for="pw-new">New password</label>
        <input type="password" id="pw-new" autocomplete="new-password" />
      </div>
      <div class="form-group">
        <label for="pw-again">Type it again</label>
        <input type="password" id="pw-again" autocomplete="new-password" />
      </div>
      <div class="form-group hidden" id="pw-code-row">
        <label for="pw-code">6-digit code from your authenticator app</label>
        <input type="text" id="pw-code" inputmode="numeric" autocomplete="one-time-code" maxlength="6" />
      </div>
      <div class="login-error hidden" id="pw-error" role="alert" style="margin-top:16px;"></div>
      <div class="crm-dialog-actions">
        ${canClose
          ? '<button type="button" class="btn-secondary" data-pw-close>Cancel</button>'
          : '<button type="button" class="btn-secondary" data-pw-signout>Sign out</button>'}
        <button type="submit" class="btn-primary" id="pw-save">Save password</button>
      </div>
    </form>`;
  document.body.appendChild(overlay);

  const $ = (sel) => overlay.querySelector(sel);
  const errorEl = $('#pw-error');
  const codeRow = $('#pw-code-row');
  const saveBtn = $('#pw-save');
  const show = (msg) => { errorEl.textContent = msg; errorEl.classList.remove('hidden'); };
  const busy = (on) => { saveBtn.disabled = on; saveBtn.textContent = on ? 'Saving…' : 'Save password'; };

  let resolveFn;
  const promise = new Promise((r) => { resolveFn = r; });
  _open = { promise };

  const onKey = (e) => { if (e.key === 'Escape' && canClose) close(false); };
  function close(saved) {
    document.removeEventListener('keydown', onKey);
    overlay.remove();
    _open = null;
    resolveFn(saved);
  }
  document.addEventListener('keydown', onKey);

  // The person's email beside the new password, so a password manager files
  // it under the right account.
  getSupabase()?.auth.getSession()
    .then(({ data }) => { $('#pw-username').value = data?.session?.user?.email || ''; })
    .catch(() => { /* only a password manager's hint */ });

  overlay.querySelectorAll('[data-pw-close]').forEach((b) => b.addEventListener('click', () => close(false)));
  if (canClose) overlay.addEventListener('mousedown', (e) => { if (e.target === overlay) close(false); });

  $('[data-pw-signout]')?.addEventListener('click', async () => {
    // Signing out here means they will sign in another way next time: stop
    // asking in this tab.
    passwordNoLongerOwed();
    try { await getSupabase().auth.signOut(); } catch { /* going to the sign-in page either way */ }
    window.location.href = 'login.html';
  });

  $('#password-form').addEventListener('submit', async (e) => {
    e.preventDefault();
    errorEl.classList.add('hidden');
    const pw = $('#pw-new').value;
    const problem = passwordProblem(pw, $('#pw-again').value);
    if (problem) { show(problem); return; }
    busy(true);
    try {
      const sb = getSupabase();
      const { data: aal } = await sb.auth.mfa.getAuthenticatorAssuranceLevel();
      if (aal?.nextLevel === 'aal2' && aal?.currentLevel !== 'aal2') {
        if (codeRow.classList.contains('hidden')) {
          codeRow.classList.remove('hidden');
          $('#pw-code').focus();
          show('Two-step sign-in is on for this account. Enter the 6-digit code from your authenticator app.');
          return;
        }
        const code = $('#pw-code').value.replace(/\D/g, '');
        if (code.length !== 6) { show('Enter the 6-digit code from your authenticator app.'); return; }
        const { data: factors } = await sb.auth.mfa.listFactors();
        const factor = (factors?.totp || []).find((f) => f.status === 'verified');
        if (!factor) { show("Couldn't find your authenticator. Sign out, then sign in again."); return; }
        const { error: codeErr } = await sb.auth.mfa.challengeAndVerify({ factorId: factor.id, code });
        if (codeErr) { show("That code didn't work. Try the newest one from your app."); return; }
      }
      const { error } = await sb.auth.updateUser({ password: pw });
      if (error) {
        console.warn('[password] save refused:', error.code || '', error.message);
        show(saveErrorWords(error));
        return;
      }
      passwordNoLongerOwed();
      close(true);
      showToast('Password saved.', 'success');
    } catch (err) {
      console.warn('[password] save failed:', /** @type {any} */ (err)?.message);
      show("Couldn't save your password. Try again.");
    } finally {
      if (_open) busy(false);
    }
  });

  setTimeout(() => $('#pw-new')?.focus(), 0);
  return promise;
}

/**
 * Arriving from a reset or invite link: ask for the password once the link's
 * sign-in is in. getSession() waits for supabase-js to finish reading the
 * link, so a session here is the link's.
 */
export async function askIfALinkOwesAPassword() {
  try {
    const sb = getSupabase();
    if (!sb) return;
    const { data } = await sb.auth.getSession();
    // No session: the CRM sends the person to the sign-in page, which reads
    // an expired link's flag itself (login.js).
    if (!data?.session) return;
    // Signed in, so an old link's "expired" flag is not news to anyone.
    takeLinkError();
    const reason = linkNeedsPassword();
    if (reason) await openPasswordDialog({ reason });
  } catch (e) {
    console.warn('[password] link check:', /** @type {any} */ (e)?.message);
  }
}

// Settings → Security → Password.
document.getElementById('btn-change-password')
  ?.addEventListener('click', () => { openPasswordDialog({ reason: 'change' }); });

// Same deferral as mfa.js: this module body runs before app.js reaches
// _config.setSupabaseRef(), and a microtask runs after it.
queueMicrotask(() => { askIfALinkOwesAPassword(); });
